/**
 * Backup Plans API client (Spec 201 — FR-033..035)
 *
 * Typed frontend client for the consolidated Backup section's plan endpoints.
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 201-backup-management-overhaul
 */

import apiClient from '../services/api';

export type PlanStatus = 'draft' | 'active' | 'paused' | 'archived';
export type BackupEntityType = 'lakehouse' | 'warehouse' | 'workspace';

export interface BackupPlan {
  id: string;
  name: string;
  description: string | null;
  status: PlanStatus;
  scheduleDefinitionId: string | null;
  retentionPolicyId: string | null;
  storageAccountId: string | null;
  scopeRuleJson: string | null;
  legalHold: boolean;
  captureDefinitions?: boolean; // Spec 205 FR-009, surfaced by Spec 214 T067
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
  // Enrichment from the list endpoint:
  targetCount?: number; // # lakehouses/warehouses tied to the plan
  backupCount?: number; // # backups of those targets
  activeBackupCount?: number; // # active (Running/Pending) backups — blocks delete when > 0
  targetTypes?: string; // Spec 205: distinct target entity types, comma-joined (lakehouse,warehouse,workspace)
}

export interface BackupPlanTarget {
  id: string;
  backupPlanId: string;
  entityType: BackupEntityType | 'workspace';
  entityId: string;
  assignmentSource: 'explicit' | 'scope_rule';
  captureEnabled?: boolean; // Spec 214 Q4: capture auto-enabled by this workspace attach
  resolutionReason: string | null;
  createdAt: string;
}

export interface BackupPlanAudit {
  id: string;
  backupPlanId: string;
  action: string;
  actor: string | null;
  detailJson: string | null;
  createdAt: string;
}

export interface BackupPlanDetail extends BackupPlan {
  targets: BackupPlanTarget[];
  audit: BackupPlanAudit[];
}

export interface EffectivePlanResolution {
  entityType: BackupEntityType;
  entityId: string;
  plan: BackupPlan | null;
  resolutionReason: string;
}

export interface CreatePlanInput {
  name: string;
  description?: string | null;
  scheduleDefinitionId?: string | null;
  retentionPolicyId?: string | null;
  storageAccountId?: string | null;
  scopeRuleJson?: string | null;
  captureDefinitions?: boolean; // Spec 214 T067: plan-level workspace definition capture toggle
}

// Spec 202 — Backup Immutability
export type ImmutabilityProbeStatus = 'ok' | 'degraded' | 'unsupported';

export interface ImmutabilityPolicy {
  backupPlanId?: string;
  mode: boolean;
  enforcementLevel?: string;
  derivedLockDays: number | null;
  overrideLockDays: number | null;
  effectiveLockDays?: number | null;
  legalHoldDefault?: boolean;
  appliedLevel?: string | null;
  earliestUnlockAt?: string | null;
  probeStatus?: ImmutabilityProbeStatus | null;
  probeReason?: string | null;
}

export interface SetImmutabilityInput {
  mode: boolean;
  overrideLockDays?: number | null;
  legalHoldDefault?: boolean;
}

type Wrapped<T> = { success: boolean; data: T };

const BASE = '/api/v1/backup/plans';

export const plansApi = {
  list: async (): Promise<BackupPlan[]> =>
    (await apiClient.get<Wrapped<BackupPlan[]>>(BASE)).data,

  get: async (id: string): Promise<BackupPlanDetail> =>
    (await apiClient.get<Wrapped<BackupPlanDetail>>(`${BASE}/${id}`)).data,

  create: async (input: CreatePlanInput): Promise<BackupPlan> =>
    (await apiClient.post<Wrapped<BackupPlan>>(BASE, input)).data,

  update: async (id: string, input: Partial<CreatePlanInput>): Promise<BackupPlan> =>
    (await apiClient.put<Wrapped<BackupPlan>>(`${BASE}/${id}`, input)).data,

  transition: async (id: string, action: 'pause' | 'activate' | 'archive'): Promise<BackupPlan> =>
    (await apiClient.post<Wrapped<BackupPlan>>(`${BASE}/${id}/${action}`)).data,

  setLegalHold: async (id: string, hold: boolean): Promise<BackupPlan> =>
    (await apiClient.post<Wrapped<BackupPlan>>(`${BASE}/${id}/legal-hold`, { hold })).data,

  // Spec 202 — immutability (WORM)
  getImmutability: async (id: string): Promise<ImmutabilityPolicy> =>
    (await apiClient.get<Wrapped<ImmutabilityPolicy>>(`${BASE}/${id}/immutability`)).data,

  setImmutability: async (id: string, input: SetImmutabilityInput): Promise<ImmutabilityPolicy> =>
    (await apiClient.put<Wrapped<ImmutabilityPolicy>>(`${BASE}/${id}/immutability`, input)).data,

  probeImmutability: async (id: string): Promise<{ status: ImmutabilityProbeStatus; reason?: string }> =>
    (await apiClient.post<Wrapped<{ status: ImmutabilityProbeStatus; reason?: string }>>(
      `${BASE}/${id}/immutability/probe`
    )).data,

  attachTarget: async (id: string, entityType: BackupEntityType | 'workspace', entityId: string): Promise<BackupPlanTarget> =>
    (await apiClient.post<Wrapped<BackupPlanTarget>>(`${BASE}/${id}/targets`, { entityType, entityId })).data,

  detachTarget: async (id: string, entityType: BackupEntityType, entityId: string): Promise<{ removed: boolean }> =>
    (await apiClient.delete<Wrapped<{ removed: boolean }>>(`${BASE}/${id}/targets/${entityType}/${entityId}`)).data,

  effectivePlan: async (entityType: BackupEntityType, entityId: string): Promise<EffectivePlanResolution> =>
    (await apiClient.get<Wrapped<EffectivePlanResolution>>(
      `/api/v1/backup/entities/${entityType}/${entityId}/effective-plan`
    )).data,

  remove: async (id: string): Promise<{ deleted: boolean }> =>
    (await apiClient.delete<Wrapped<{ deleted: boolean }>>(`${BASE}/${id}`)).data,
};
